import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Pencil } from "lucide-react";

export function EditBookingDialog({ booking, open, onClose, api, onSaved }) {
  const [form, setForm] = useState({
    meeting_name: "",
    notes: "",
    start_time: "",
    end_time: "",
    date: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (booking && open) {
      setForm({
        meeting_name: booking.meeting_name || "",
        notes: booking.notes || "",
        date: booking.start_datetime?.slice(0, 10) || "",
        start_time: booking.start_datetime?.slice(11, 16) || "",
        end_time: booking.end_datetime?.slice(11, 16) || "",
      });
    }
  }, [booking, open]);

  const handleSave = async () => {
    const payload = {};
    if (form.meeting_name !== (booking.meeting_name || "")) payload.meeting_name = form.meeting_name;
    if (form.notes !== (booking.notes || "")) payload.notes = form.notes;

    const origDate = booking.start_datetime?.slice(0, 10);
    const origStart = booking.start_datetime?.slice(11, 16);
    const origEnd = booking.end_datetime?.slice(11, 16);

    if (form.date !== origDate || form.start_time !== origStart || form.end_time !== origEnd) {
      if (!form.date || !form.start_time || !form.end_time) {
        toast.error("Date and times are required");
        return;
      }
      payload.start_datetime = `${form.date}T${form.start_time}:00`;
      payload.end_datetime = `${form.date}T${form.end_time}:00`;
    }

    if (Object.keys(payload).length === 0) {
      toast.info("No changes made");
      onClose();
      return;
    }

    setSaving(true);
    try {
      await api("put", `/bookings/${booking.id}/edit`, payload);
      toast.success("Booking updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update booking");
    } finally {
      setSaving(false);
    }
  };

  if (!booking) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="edit-booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Pencil className="h-4 w-4" /> Edit Booking
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-sm font-medium">{booking.space_name}</p>
            {booking.booked_for_name && <p className="text-xs text-rig-teal">Booked for: {booking.booked_for_name}</p>}
            <p className="text-xs text-muted-foreground">Ref: {booking.id?.slice(0, 8)}</p>
          </div>

          <div>
            <Label>Meeting Name</Label>
            <Input
              value={form.meeting_name}
              onChange={e => setForm(f => ({ ...f, meeting_name: e.target.value }))}
              placeholder="e.g. Weekly Standup"
              data-testid="edit-meeting-name"
            />
          </div>

          <div>
            <Label>Date</Label>
            <Input
              type="date"
              value={form.date}
              onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              data-testid="edit-date"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start Time</Label>
              <Input
                type="time"
                value={form.start_time}
                onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
                data-testid="edit-start-time"
              />
            </div>
            <div>
              <Label>End Time</Label>
              <Input
                type="time"
                value={form.end_time}
                onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))}
                data-testid="edit-end-time"
              />
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <Input
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Any special requirements..."
              data-testid="edit-notes"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="edit-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-foreground text-background hover:bg-foreground/90 rounded-none"
              data-testid="edit-save-btn"
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
